/**
 * Choose a photograph, frame it, shrink it here, send the small one.
 *
 * The resizing happens in the browser for two reasons. A phone camera produces three or four
 * megabytes and an office at the gate has the worst connection of anybody's day, so sending the
 * original is a minute of waiting for a picture that will be displayed at forty pixels. And doing
 * it on the server would mean a native image library — the kind of dependency that stopped half
 * the test suite from running on the owner's own laptop this morning.
 *
 * Before anything is sent, the picture opens in a square frame to move and zoom (15 October 2026:
 * the owner found the automatic centre square often cut the face off, with no way to choose). What
 * is in the frame is drawn to a 512px canvas as JPEG. The server still checks the size and reads
 * the format from the bytes, because everything here can be replaced by whoever holds the phone.
 */
import { useEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { API_URL } from './api.js';
import { Camera, Trash2 } from 'lucide-react';
import { api, del } from './api.js';
import { errorMessage } from './passkey.js';
import { Modal } from './Modal.js';
import { t } from '../i18n/index.js';

/** The longest side of what we send. Displayed at 40–96px, printed at nothing. */
const SIZE = 512;
const QUALITY = 0.82;
/** The crop frame's size on screen, in CSS pixels. */
const FRAME = 280;

type Pt = { x: number; y: number };

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () =>
      el.naturalWidth && el.naturalHeight ? resolve(el) : reject(new Error(t('photo_unreadable')));
    el.onerror = () => reject(new Error(t('photo_unreadable')));
    el.src = url;
  });
}

/** The square at (sx, sy) of side `side`, in the picture's own pixels, as a 512px JPEG. */
async function cropJpeg(
  img: HTMLImageElement,
  sx: number,
  sy: number,
  side: number,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(t('photo_unreadable'));
  ctx.drawImage(img, sx, sy, side, side, 0, 0, SIZE, SIZE);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY),
  );
  if (!blob) throw new Error(t('photo_unreadable'));
  return blob;
}

/**
 * Move and zoom the picture inside a square frame; the circle shows how the face will look. One
 * finger (or the mouse) drags, two fingers pinch, and the slider zooms for anyone who would rather
 * not pinch. The picture always covers the frame, so there is never an empty corner.
 */
function PhotoCropper({
  img,
  busy,
  onCancel,
  onSave,
}: {
  img: HTMLImageElement;
  busy: boolean;
  onCancel: () => void;
  onSave: (sx: number, sy: number, side: number) => void;
}) {
  const base = FRAME / Math.min(img.naturalWidth, img.naturalHeight);
  const [zoom, setZoom] = useState(1);
  const k = base * zoom;
  const clamp = (x: number, y: number, kk: number): Pt => ({
    x: Math.min(0, Math.max(FRAME - img.naturalWidth * kk, x)),
    y: Math.min(0, Math.max(FRAME - img.naturalHeight * kk, y)),
  });
  // Centred to begin with: the old automatic crop, which is right more often than not.
  const [pos, setPos] = useState<Pt>(() => ({
    x: (FRAME - img.naturalWidth * base) / 2,
    y: (FRAME - img.naturalHeight * base) / 2,
  }));
  const pointers = useRef(new Map<number, Pt>());
  const pinch = useRef<{ dist: number; zoom: number } | null>(null);

  /** Zoom about the frame's centre, keeping what is in the middle in the middle. */
  const zoomTo = (z: number) => {
    const nz = Math.min(4, Math.max(1, z));
    const nk = base * nz;
    const cx = (FRAME / 2 - pos.x) / k;
    const cy = (FRAME / 2 - pos.y) / k;
    setZoom(nz);
    setPos(clamp(FRAME / 2 - cx * nk, FRAME / 2 - cy * nk, nk));
  };
  const twoPoints = () => [...pointers.current.values()] as [Pt, Pt];

  const down = (e: RPointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [p, q] = twoPoints();
      pinch.current = { dist: Math.hypot(p.x - q.x, p.y - q.y), zoom };
    }
  };
  const move = (e: RPointerEvent<HTMLDivElement>) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const [p, q] = twoPoints();
      zoomTo((pinch.current.zoom * Math.hypot(p.x - q.x, p.y - q.y)) / pinch.current.dist);
      return;
    }
    setPos((cur) => clamp(cur.x + e.clientX - prev.x, cur.y + e.clientY - prev.y, k));
  };
  const up = (e: RPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  return (
    <Modal
      open
      title={t('photo_crop_title')}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
            {t('cancel')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onSave(-pos.x / k, -pos.y / k, FRAME / k)}
          >
            {busy ? t('photo_saving') : t('photo_use')}
          </button>
        </>
      }
    >
      <p className="muted small-text">{t('photo_crop_hint')}</p>
      <div
        className="photo-crop"
        style={{ width: FRAME, height: FRAME }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        role="img"
        aria-label={t('photo_crop_title')}
      >
        <img
          src={img.src}
          alt=""
          draggable={false}
          style={{
            width: img.naturalWidth * k,
            height: img.naturalHeight * k,
            transform: `translate(${pos.x}px, ${pos.y}px)`,
          }}
        />
        <span className="photo-crop-ring" aria-hidden="true" />
      </div>
      <label className="photo-crop-zoom">
        {t('photo_zoom')}
        <input
          type="range"
          min={1}
          max={4}
          step={0.01}
          value={zoom}
          onChange={(e) => zoomTo(Number(e.target.value))}
        />
      </label>
    </Modal>
  );
}

/**
 * The address of somebody's photograph, or null when they have none.
 *
 * `updatedAt` is in the query string rather than only in the ETag because a photograph is
 * replaced in place: same person, same URL, different face. With the timestamp there, a change
 * is a cache miss by construction, and a face that has not changed is still served from the
 * browser's own cache for a day.
 */
export function photoSrc(path: string, updatedAt: string | null | undefined): string | null {
  return updatedAt ? `${API_URL}${path}?v=${encodeURIComponent(updatedAt)}` : null;
}

export function PhotoPicker({
  url,
  hasPhoto,
  onDone,
  label,
}: {
  /** Where to PUT the photo, and DELETE it: /me/photo, or /admin/workers/:id/photo. */
  url: string;
  hasPhoto: boolean;
  /** Called after a change, so the page can reload the face it is showing. */
  onDone: () => void;
  label?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The chosen picture, waiting in the crop frame. */
  const [chosen, setChosen] = useState<HTMLImageElement | null>(null);
  useEffect(
    () => () => {
      if (chosen) URL.revokeObjectURL(chosen.src);
    },
    [chosen],
  );

  const choose = async (file: File) => {
    setError(null);
    try {
      setChosen(await loadImage(URL.createObjectURL(file)));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      if (input.current) input.current.value = '';
    }
  };

  const send = async (img: HTMLImageElement, sx: number, sy: number, side: number) => {
    setBusy(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('photo', await cropJpeg(img, sx, sy, side), 'photo.jpg');
      // api() leaves FormData alone: forcing a JSON content type here corrupts the boundary, and
      // it already turns a refusal into the message the server wrote.
      await api(url, { method: 'PUT', body });
      setChosen(null);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await del(url);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="photo-picker">
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void choose(file);
        }}
      />
      <button
        type="button"
        className="small secondary"
        disabled={busy}
        onClick={() => input.current?.click()}
      >
        <Camera size={15} aria-hidden="true" />{' '}
        {busy ? t('photo_saving') : (label ?? (hasPhoto ? t('photo_change') : t('photo_add')))}
      </button>
      {hasPhoto && !busy && (
        <button type="button" className="link small" onClick={() => void remove()}>
          <Trash2 size={14} aria-hidden="true" /> {t('photo_remove')}
        </button>
      )}
      {error && <span className="field-error">{error}</span>}
      {chosen && (
        <PhotoCropper
          img={chosen}
          busy={busy}
          onCancel={() => setChosen(null)}
          onSave={(sx, sy, side) => void send(chosen, sx, sy, side)}
        />
      )}
    </span>
  );
}
